import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPuzzlePiece,
    faUserCircle,
    faCheckCircle,
    faCircleHalfStroke,
    faBox,
    faBoxOpen,
    faTrash,
    faCheck,
    faSortUp,
    faSortDown,
} from '@fortawesome/free-solid-svg-icons';

/**
 * Sortable column header for the list-view table.
 * Defined as a named component (not nested inside a render function)
 * so React can properly reconcile it across renders.
 */
export const SortHeader = ({ column, label, className = '', centerText = false, listSortColumn, listSortDir, onSort }) => {
    const isActive = listSortColumn === column;
    return (
        <th
            className={`px-3 py-3 text-xs font-semibold uppercase tracking-wider cursor-pointer select-none transition-colors whitespace-nowrap ${
                centerText ? 'text-center' : 'text-left'
            } ${
                isActive
                    ? 'text-red-600 bg-red-50 hover:bg-red-100'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            } ${className}`}
            onClick={() => onSort(column)}
        >
            <div className={`flex items-center gap-1 ${centerText ? 'justify-center' : ''}`}>
                <span>{label}</span>
                <FontAwesomeIcon
                    icon={isActive ? (listSortDir === 'asc' ? faSortUp : faSortDown) : faSortDown}
                    className={`text-xs ${isActive ? 'text-red-500' : 'text-gray-300'}`}
                />
            </div>
        </th>
    );
};

/**
 * A single row in the list-view table.
 * Defined as a named component (not nested inside renderListView)
 * so React's reconciliation works correctly.
 */
export const ListRow = ({
    set,
    isOwner,
    isBulkMode,
    selectedSets,
    selectedSet,
    toggleBulkSelect,
    toggleSelectSet,
    isWishlist,
    showPrices,
    hasMinifigures,
    moveToCollection,
    handleRemoveClick,
    openStatusModal,
}) => {
    const isBulkSelected = isBulkMode && selectedSets.has(set.set_num);
    // isSelected only applies to wishlist rows (expand button behaviour)
    const isSelected = !isBulkMode && isWishlist && selectedSet === set.set_num;
    const isIncomplete = !isWishlist && Number(set.complete) < Number(set.quantity);

    const handleClick = () => {
        if (isBulkMode && isOwner) {
            toggleBulkSelect(set.set_num);
        } else if (isOwner && !isWishlist) {
            openStatusModal(set);
        } else {
            toggleSelectSet(set.set_num);
        }
    };

    const handleImageError = (e) => {
        e.target.src = '/images/lego_piece_questionmark.png';
    };

    return (
        <tr
            className={`border-b border-gray-100 transition-colors duration-150 cursor-pointer ${
                isBulkSelected
                    ? 'bg-blue-50 hover:bg-blue-100'
                    : isSelected
                        ? 'bg-red-50'
                        : 'hover:bg-gray-50'
            } ${isIncomplete ? 'border-l-[3px] border-l-amber-400' : ''}`}
            onClick={handleClick}
        >
            {/* Bulk checkbox */}
            {isBulkMode && isOwner && (
                <td className="w-10 px-3 py-2">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-150 ${
                        isBulkSelected ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'
                    }`}>
                        {isBulkSelected && <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />}
                    </div>
                </td>
            )}

            {/* Thumbnail */}
            <td className="px-2 py-1.5 w-14">
                <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100 overflow-hidden flex-shrink-0">
                    <img
                        src={set.img_url}
                        alt={set.name}
                        className="max-w-full max-h-full object-contain"
                        onError={handleImageError}
                    />
                </div>
            </td>

            {/* Set # */}
            <td className="px-3 py-2 whitespace-nowrap">
                <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-mono text-gray-500">{set.set_num}</span>
                    {!isWishlist && Number(set.quantity) > 1 && (
                        <span className="inline-flex items-center bg-amber-100 text-amber-800 text-xs font-bold px-1.5 py-0.5 rounded-full w-fit">
                            ×{set.quantity}
                        </span>
                    )}
                </div>
            </td>

            {/* Name + inline owner actions (wishlist only) */}
            <td className="px-3 py-2 min-w-[160px] relative">
                <div className={`${isOwner && !isBulkMode && isWishlist ? 'pr-28' : ''}`}>
                    <span className="text-sm font-semibold text-gray-800 leading-tight">{set.name}</span>
                </div>

                {isOwner && !isBulkMode && isWishlist && (
                    <div className={`absolute top-1/2 right-3 -translate-y-1/2 flex items-center gap-1.5 transition-all duration-150 ${
                        isSelected ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none'
                    }`}>
                        <button
                            className="px-2.5 py-1.5 bg-yellow-400 hover:bg-yellow-500 text-gray-800 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 shadow-sm"
                            onClick={e => { e.stopPropagation(); moveToCollection(set.set_num); }}
                        >
                            <FontAwesomeIcon icon={faBoxOpen} className="text-xs" />
                            <span>Move</span>
                        </button>
                        <button
                            className="px-2.5 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 shadow-sm"
                            onClick={e => handleRemoveClick(set, e)}
                            title="Remove"
                        >
                            <FontAwesomeIcon icon={faTrash} className="text-xs" />
                        </button>
                    </div>
                )}
            </td>

            {/* Year */}
            <td className="px-3 py-2 text-center whitespace-nowrap">
                <span className="text-sm text-gray-600 font-medium bg-gray-100 px-2 py-0.5 rounded-md">{set.year}</span>
            </td>

            {/* Pieces */}
            <td className="px-3 py-2 text-center whitespace-nowrap">
                <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-xs font-semibold px-2 py-1 rounded-lg border border-red-100">
                    <FontAwesomeIcon icon={faPuzzlePiece} className="text-red-400 text-xs" />
                    {Number(set.num_parts).toLocaleString()}
                </span>
            </td>

            {/* Minifigs */}
            {hasMinifigures && (
                <td className="px-3 py-2 text-center whitespace-nowrap">
                    {Number(set.num_minifigures) > 0 ? (
                        <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 text-xs font-semibold px-2 py-1 rounded-lg border border-purple-100">
                            <FontAwesomeIcon icon={faUserCircle} className="text-purple-400 text-xs" />
                            {!isWishlist && Number(set.expected_minifigures) > 0
                                ? `${set.owned_minifigures}/${set.expected_minifigures}`
                                : set.num_minifigures
                            }
                        </span>
                    ) : (
                        <span className="text-gray-300 text-xs">—</span>
                    )}
                </td>
            )}

            {/* Status — collection only */}
            {!isWishlist && (
                <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg border whitespace-nowrap ${
                            Number(set.complete) === Number(set.quantity)
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : Number(set.complete) > 0
                                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                                    : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}>
                            <FontAwesomeIcon
                                icon={Number(set.complete) === Number(set.quantity) ? faCheckCircle : faCircleHalfStroke}
                                className={`text-xs ${
                                    Number(set.complete) === Number(set.quantity)
                                        ? 'text-emerald-400'
                                        : Number(set.complete) > 0
                                            ? 'text-blue-400'
                                            : 'text-amber-400'
                                }`}
                            />
                            {Number(set.complete) === Number(set.quantity)
                                ? 'Complete'
                                : Number(set.complete) > 0
                                    ? `${set.complete}/${set.quantity} built`
                                    : 'Incomplete'
                            }
                        </span>
                        {Number(set.sealed) > 0 && (
                            <span className="inline-flex items-center gap-1 bg-sky-50 text-sky-700 border border-sky-200 text-xs font-semibold px-2 py-1 rounded-lg whitespace-nowrap">
                                <FontAwesomeIcon icon={faBox} className="text-sky-400 text-xs" />
                                {Number(set.sealed) === Number(set.quantity) ? 'Sealed' : `${set.sealed} sealed`}
                            </span>
                        )}
                    </div>
                </td>
            )}

            {/* Prices */}
            {showPrices && (
                <>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                        <span className="text-sm text-gray-700 font-medium">
                            {set.retail_price ? `$${Number(set.retail_price).toFixed(2)}` : <span className="text-gray-300">—</span>}
                        </span>
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                        <span className="text-sm text-green-600 font-semibold">
                            {set.sealed_value ? `$${Number(set.sealed_value).toFixed(2)}` : <span className="text-gray-300">—</span>}
                        </span>
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                        <span className="text-sm text-blue-600 font-semibold">
                            {set.used_value ? `$${Number(set.used_value).toFixed(2)}` : <span className="text-gray-300">—</span>}
                        </span>
                    </td>
                </>
            )}
        </tr>
    );
};
